import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import Layout from '../../components/layout/Layout';
import PatientDetailsModal from '../../components/modals/PatientDetailsModal';
import PatientEditModal from '../../components/modals/PatientEditModal';
import MessageModal from '../../components/modals/MessageModal';
import InputMask from 'react-input-mask';
import Button from '../../components/common/Button'; // Importando o botão genérico
import './PacientesPage.css';

function PacientesPage() {
  const [pacientes, setPacientes] = useState([]);
  const [clinicas, setClinicas] = useState([]);
  const [novoPaciente, setNovoPaciente] = useState({
    nome: '',
    cpf: '',
    telefone: '',
    email: '',
    endereco: '',
    clinica: '',
    plano: '',
    dataNascimento: '',
    dataAtendimento: '',
  });
  const [error, setError] = useState('');
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pacienteToRemove, setPacienteToRemove] = useState(null);
  const [camposVisiveis] = useState({
    nome: true,
    cpf: true,
    telefone: true,
    email: true,
    endereco: true,
    clinica: true,
    plano: true,
    dataNascimento: true,
    dataAtendimento: true,
  });
  const [user] = useAuthState(auth);

  useEffect(() => {
    if (user) {
      const fetchPacientes = async () => {
        const q = query(
          collection(db, 'pacientes'),
          where('userId', '==', user.uid)
        );
        const querySnapshot = await getDocs(q);
        const fetchedPacientes = [];
        querySnapshot.forEach((doc) => {
          fetchedPacientes.push({ id: doc.id, ...doc.data() });
        });
        setPacientes(fetchedPacientes);
      };

      const fetchClinicas = async () => {
        const clinicasQuery = query(
          collection(db, 'clinicas'),
          where('userId', '==', user.uid)
        );
        const querySnapshot = await getDocs(clinicasQuery);
        const fetchedClinicas = [];
        querySnapshot.forEach((doc) => {
          fetchedClinicas.push({ id: doc.id, ...doc.data() });
        });
        setClinicas(fetchedClinicas);
      };

      fetchPacientes();
      fetchClinicas();
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNovoPaciente({ ...novoPaciente, [name]: value });
  };

  const handleClinicaChange = async (e) => {
    const { value } = e.target;
    setNovoPaciente({ ...novoPaciente, clinica: value });

    const existingClinica = clinicas.find(
      (clinica) => clinica.nome.toLowerCase() === value.toLowerCase()
    );
    if (!existingClinica) {
      const newClinicaRef = await addDoc(collection(db, 'clinicas'), {
        nome: value,
      });
      setClinicas([...clinicas, { id: newClinicaRef.id, nome: value }]);
    }
  };

  const adicionarPaciente = async () => {
    if (novoPaciente.nome) {
      try {
        const newPacienteRef = doc(collection(db, 'pacientes'));
        await setDoc(newPacienteRef, {
          ...novoPaciente,
          userId: user.uid,
          id: newPacienteRef.id,
        });
        setPacientes([
          ...pacientes,
          { ...novoPaciente, id: newPacienteRef.id },
        ]);
        setNovoPaciente({
          nome: '',
          cpf: '',
          telefone: '',
          email: '',
          endereco: '',
          clinica: '',
          plano: '',
          dataNascimento: '',
          dataAtendimento: '',
        });
        setError('');
      } catch (error) {
        console.error('Erro ao adicionar paciente: ', error);
        setError('Erro ao adicionar paciente. Tente novamente.');
      }
    } else {
      setError('Por favor, preencha o nome do paciente.');
    }
  };

  const handleRemoveClick = (paciente) => {
    setPacienteToRemove(paciente);
    setIsModalOpen(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteDoc(doc(db, 'pacientes', pacienteToRemove.id));
      setPacientes(
        pacientes.filter((paciente) => paciente.id !== pacienteToRemove.id)
      );
      setIsModalOpen(false);
    } catch (error) {
      console.error('Erro ao remover paciente: ', error);
      setError('Erro ao remover paciente. Tente novamente.');
    }
  };

  const handleCancelRemove = () => {
    setIsModalOpen(false);
    setPacienteToRemove(null);
  };

  const visualizarPaciente = (paciente) => {
    setSelectedPaciente(paciente);
    setShowModal(true);
  };

  const editarPaciente = (paciente) => {
    setSelectedPaciente(paciente);
    setShowEditModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setSelectedPaciente(null);
  };

  const fecharEditModal = () => {
    setShowEditModal(false);
    setSelectedPaciente(null);
  };

  const handleSaveEdit = async (updatedPaciente) => {
    try {
      const pacienteRef = doc(db, 'pacientes', selectedPaciente.id);
      await updateDoc(pacienteRef, updatedPaciente);
      setPacientes(
        pacientes.map((p) =>
          p.id === selectedPaciente.id
            ? { ...updatedPaciente, id: selectedPaciente.id }
            : p
        )
      );
      fecharEditModal();
    } catch (error) {
      console.error('Erro ao atualizar paciente: ', error);
      setError('Erro ao atualizar paciente. Tente novamente.');
    }
  };

  const handleSaveObservacao = async (pacienteId, observacao) => {
    try {
      const pacienteRef = doc(db, 'pacientes', pacienteId);
      await updateDoc(pacienteRef, { observacoes: observacao });
      setPacientes(
        pacientes.map((p) =>
          p.id === pacienteId ? { ...p, observacoes: observacao } : p
        )
      );
    } catch (error) {
      console.error('Erro ao salvar observação:', error);
    }
  };

  return (
    <Layout>
      <h2 className="text-3xl font-semibold mb-6">Cadastro de Pacientes</h2>
      <section className="pacientes-page">
        {error && (
          <div className="error-message" aria-live="polite">
            {error}
          </div>
        )}
        {pacientes.length > 0 ? (
          <ul className="pacientes-list">
            {pacientes.map((paciente) => (
              <li key={paciente.id} className="paciente-item">
                <div className="paciente-info">
                  {camposVisiveis.nome && <span>{paciente.nome}</span>} -{' '}
                  {camposVisiveis.clinica && <span>{paciente.clinica}</span>} -{' '}
                  {camposVisiveis.cpf && <span>{paciente.cpf}</span>}
                </div>
                <div className="paciente-actions">
                  <Button
                    type="pesquisar"
                    label="Visualizar"
                    onClick={() => visualizarPaciente(paciente)}
                  />
                  <Button
                    type="editar"
                    label="Editar"
                    onClick={() => editarPaciente(paciente)}
                  />
                  <Button
                    type="remover"
                    label="Remover"
                    onClick={() => handleRemoveClick(paciente)}
                  />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>Nenhum paciente cadastrado.</p>
        )}

        <section className="adicionar-paciente">
          <h3>Adicionar Paciente</h3>
          <div className="input-grid">
            <div className="input-group">
              <label htmlFor="nome">Nome:</label>
              <input
                type="text"
                id="nome"
                name="nome"
                placeholder="Nome"
                value={novoPaciente.nome}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="cpf">CPF:</label>
              <InputMask
                mask="999.999.999-99"
                value={novoPaciente.cpf}
                onChange={handleInputChange}
              >
                {(inputProps) => (
                  <input
                    {...inputProps}
                    type="text"
                    id="cpf"
                    name="cpf"
                    placeholder="CPF"
                  />
                )}
              </InputMask>
            </div>

            <div className="input-group">
              <label htmlFor="clinica">Clínica/Atendimento:</label>
              <input
                type="text"
                id="clinica"
                name="clinica"
                placeholder="Clínica de Atendimento"
                value={novoPaciente.clinica}
                onChange={handleClinicaChange}
                list="clinicas-list"
              />
              <datalist id="clinicas-list">
                {clinicas.map((clinica) => (
                  <option key={clinica.id} value={clinica.nome} />
                ))}
              </datalist>
            </div>

            <div className="input-group">
              <label htmlFor="plano">Plano de Saúde:</label>
              <input
                type="text"
                id="plano"
                name="plano"
                placeholder="Plano de Saúde"
                value={novoPaciente.plano}
                onChange={handleInputChange}
              />
            </div>

            <div className="input-group">
              <label htmlFor="telefone">Telefone:</label>
              <InputMask
                mask={
                  novoPaciente.telefone.length > 10
                    ? '(99) 99999-9999'
                    : '(99) 9999-9999'
                }
                value={novoPaciente.telefone}
                onChange={handleInputChange}
              >
                {(inputProps) => (
                  <input
                    {...inputProps}
                    type="text"
                    id="telefone"
                    name="telefone"
                    placeholder="Telefone"
                  />
                )}
              </InputMask>
            </div>

            <div className="input-group">
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email"
                value={novoPaciente.email}
                onChange={handleInputChange}
              />
            </div>

            <div className="input-group">
              <label htmlFor="endereco">Endereço:</label>
              <input
                type="text"
                id="endereco"
                name="endereco"
                placeholder="Endereço"
                value={novoPaciente.endereco}
                onChange={handleInputChange}
              />
            </div>

            <div className="input-group">
              <label htmlFor="dataNascimento">Data de Nascimento:</label>
              <input
                type="date"
                id="dataNascimento"
                name="dataNascimento"
                value={novoPaciente.dataNascimento}
                onChange={handleInputChange}
              />
            </div>

            <div className="input-group">
              <label htmlFor="dataAtendimento">Data de Atendimento:</label>
              <input
                type="date"
                id="dataAtendimento"
                name="dataAtendimento"
                value={novoPaciente.dataAtendimento}
                onChange={handleInputChange}
              />
            </div>

            <div className="input-group input-group-full">
              <label htmlFor="observacoes">Anamnese/Observações:</label>
              <textarea
                id="observacoes"
                name="observacoes"
                placeholder="Observações"
                value={novoPaciente.observacoes}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <Button
            type="salvar"
            label="Adicionar"
            onClick={adicionarPaciente}
            className="mt-4"
          />
        </section>

        {showModal && (
          <PatientDetailsModal
            paciente={selectedPaciente}
            onClose={fecharModal}
            isOpen={showModal}
            onSaveObservacao={handleSaveObservacao}
          />
        )}

        {showEditModal && (
          <PatientEditModal
            paciente={selectedPaciente}
            onClose={fecharEditModal}
            isOpen={showEditModal}
            onSave={handleSaveEdit}
          />
        )}
      </section>

      <MessageModal
        isOpen={isModalOpen}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title="Confirmação de Remoção"
        message={`Tem certeza que deseja remover o paciente: ${pacienteToRemove?.nome}?`}
      />
    </Layout>
  );
}

export default PacientesPage;
